<div class="refund-container">
  <!-- Page Header -->
  <div class="refund-header">
    <a routerLink="/home/gallery" class="back-link">
      <svg class="back-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
      </svg>
      Back to Gallery
    </a>
    <h1 class="refund-title">Experiencing Issues?</h1>
    <p class="refund-subtitle">
      Report a failed or interrupted photobooth session to receive a replacement session voucher.
    </p>
  </div>

  <!-- STEP 1: PATH SELECTION -->
  <div *ngIf="currentStep === 'select-path'" class="card form-card">
    <!-- Policy Notice -->
    <div class="policy-callout">
      <div class="callout-icon-wrap">
        <svg class="info-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      </div>
      <div class="callout-content">
        <div class="callout-title">Refund Policy Notice</div>
        <p class="callout-text">
          Approved refunds are issued exclusively as <strong>single-use PhotoLavie coupons</strong> sent to your Google account (<strong>{{ userEmail }}</strong>). We do not process cash or card reversals.
        </p>
      </div>
    </div>

    <div class="section-block">
      <label class="section-label">How would you like to identify your session?</label>
      <div class="path-selection-grid">
        <button 
          type="button" 
          class="path-choice-btn" 
          (click)="goToScanQr()">
          <div class="choice-icon">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </div>
          <div class="choice-text">
            <div class="choice-title">I have the photo-strip QR</div>
            <div class="choice-sub">Scan the QR code printed on your photo-strip using your camera</div>
          </div>
          <div class="choice-arrow">→</div>
        </button>

        <button 
          type="button" 
          class="path-choice-btn" 
          (click)="goToManualInfo()">
          <div class="choice-icon">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
            </svg>
          </div>
          <div class="choice-text">
            <div class="choice-title">I do not have the photo-strip QR</div>
            <div class="choice-sub">Manual search using booth location, approximate date, and time</div>
          </div>
          <div class="choice-arrow">→</div>
        </button>
      </div>
    </div>
  </div>

  <!-- STEP 2A: SCAN PHOTO-STRIP QR -->
  <div *ngIf="currentStep === 'scan-qr'" class="card form-card">
    <div class="step-nav-header">
      <button type="button" class="back-step-btn" (click)="goBack()">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back to Options
      </button>
      <button 
        type="button" 
        class="text-link-btn" 
        (click)="toggleManualInputs()">
        {{ showManualQrInputs ? '📷 Use Camera Scanner' : '✏️ Enter Details Manually' }}
      </button>
    </div>

    <div class="step-title-block">
      <h2 class="step-title">Scan Photo-Strip QR Code</h2>
      <p class="step-subtitle">Point your camera at the QR code printed on your physical photo-strip.</p>
    </div>

    <!-- Live Camera Viewfinder (Immediate top visibility) -->
    <div *ngIf="!showManualQrInputs && !cameraError" class="camera-viewfinder-container">
      <video #scannerVideo class="camera-video" playsinline muted autoplay></video>
      <canvas #scannerCanvas class="scanner-canvas-hidden"></canvas>
      <div class="scanner-overlay">
        <div class="scanner-reticle">
          <div class="reticle-corner top-left"></div>
          <div class="reticle-corner top-right"></div>
          <div class="reticle-corner bottom-left"></div>
          <div class="reticle-corner bottom-right"></div>
          <div class="scanner-laser"></div>
        </div>
        <p class="scanner-instruction">Align QR code inside the frame</p>
      </div>
    </div>

    <!-- Camera Error State -->
    <div *ngIf="cameraError && !showManualQrInputs" class="camera-error-card">
      <svg class="error-icon" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <div class="error-details">
        <div class="error-title">Camera Permission Needed</div>
        <p class="error-desc">{{ cameraError }}</p>
      </div>
      <button type="button" class="btn btn-primary" (click)="toggleManualInputs()">Enter Details Manually</button>
    </div>

    <!-- Manual QR Input Fallback -->
    <div *ngIf="showManualQrInputs" class="section-block manual-qr-block">
      <div class="form-grid">
        <div class="form-group">
          <label for="deviceIdInput" class="field-label">Booth / Machine ID</label>
          <input 
            id="deviceIdInput" 
            type="text" 
            class="form-input" 
            placeholder="e.g. HMSANJOSE01" 
            [(ngModel)]="deviceId" />
        </div>
        <div class="form-group">
          <label for="sessionIdInput" class="field-label">Session ID</label>
          <input 
            id="sessionIdInput" 
            type="text" 
            class="form-input" 
            placeholder="e.g. abc-1234-xyz" 
            [(ngModel)]="sessionId" />
        </div>
        <div class="form-group full-width">
          <label for="sessionDateInput" class="field-label">Session Date (Optional)</label>
          <input 
            id="sessionDateInput" 
            type="date" 
            class="form-input" 
            [(ngModel)]="sessionDate" />
        </div>
      </div>

      <div *ngIf="submissionError" class="error-banner">
        <span>{{ submissionError }}</span>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-primary submit-btn" (click)="continueFromManualQrInput()">
          Continue to Reason Selection →
        </button>
      </div>
    </div>
  </div>

  <!-- STEP 2B: MANUAL GUIDED DETAILS (NO-QR) -->
  <div *ngIf="currentStep === 'manual-info'" class="card form-card">
    <div class="step-nav-header">
      <button type="button" class="back-step-btn" (click)="goBack()">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back to Options
      </button>
    </div>

    <div class="step-title-block">
      <h2 class="step-title">Session Information</h2>
      <p class="step-subtitle">Please provide details to help us locate your transaction.</p>
    </div>

    <div class="warning-disclosure">
      <p>
        Without the photo-strip QR code, manual review may take longer to locate and verify your transaction in our records.
      </p>
    </div>

    <div class="form-grid">
      <div class="form-group full-width">
        <label for="storeSelect" class="field-label">Store Location <span class="required-star">*</span></label>
        <select 
          id="storeSelect" 
          class="form-select" 
          [(ngModel)]="selectedStoreDocId" 
          (change)="onStoreSelected()">
          <option value="" disabled selected>
            {{ isLoadingStores ? 'Loading stores...' : 'Select your store location...' }}
          </option>
          <option *ngFor="let s of activeStores" [value]="s.id">
            {{ s.friendlyName || s.friendlyStoreName || s.name }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label for="approxDateInput" class="field-label">Date of Session</label>
        <input 
          id="approxDateInput" 
          type="date" 
          class="form-input" 
          [(ngModel)]="approximateDate" />
      </div>
      <div class="form-group">
        <label for="approxTimeInput" class="field-label">Approximate Time</label>
        <input 
          id="approxTimeInput" 
          type="time" 
          class="form-input" 
          [(ngModel)]="approximateTime" />
      </div>
      <div class="form-group">
        <label for="paymentMethodSelect" class="field-label">Payment Method</label>
        <select id="paymentMethodSelect" class="form-select" [(ngModel)]="paymentMethod">
          <option value="Card">Credit / Debit Card</option>
          <option value="Cash">Cash (Bill Acceptor)</option>
        </select>
      </div>
      <div class="form-group">
        <label for="paidAmountInput" class="field-label">Amount Paid ($ USD)</label>
        <input 
          id="paidAmountInput" 
          type="number" 
          step="0.01" 
          min="1" 
          class="form-input" 
          placeholder="10.00" 
          [(ngModel)]="paidAmount" />
      </div>
    </div>

    <div *ngIf="submissionError" class="error-banner">
      <span>{{ submissionError }}</span>
    </div>

    <div class="form-actions">
      <button type="button" class="btn btn-primary submit-btn" (click)="continueFromManualInfo()">
        Continue to Reason Selection →
      </button>
    </div>
  </div>

  <!-- STEP 3: REASON & PROBLEM SELECTION -->
  <div *ngIf="currentStep === 'select-reason'" class="card form-card">
    <div class="step-nav-header">
      <button type="button" class="back-step-btn" (click)="goBack()">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back to Session Info
      </button>
    </div>

    <!-- Identified Session Summary Pill Card -->
    <div class="identified-session-card">
      <div class="session-badge-wrap">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#16a34a" stroke-width="2.5">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>
      <div class="session-info-text">
        <div class="session-info-main" *ngIf="activePath === 'qr'">
          Photo Session: <strong>{{ getFriendlyDate(sessionDate || approximateDate) }}</strong>
        </div>
        <div class="session-info-date" *ngIf="activePath === 'qr'">
          ✓ Photo strip QR verified
        </div>
        <div class="session-info-main" *ngIf="activePath === 'no-qr'">
          Location: <strong>{{ selectedStoreFriendlyName || boothLocation }}</strong>
          <span *ngIf="paidAmount">• ${{ paidAmount }}</span>
        </div>
        <div class="session-info-date" *ngIf="activePath === 'no-qr'">
          Session Date: <strong>{{ getFriendlyDate(sessionDate || approximateDate) }}</strong>
        </div>
      </div>
      <button type="button" class="change-session-btn" (click)="goBack()">
        {{ activePath === 'qr' ? 'Re-scan' : 'Edit' }}
      </button>
    </div>

    <div class="section-block">
      <label class="section-label">What issue occurred during your session?</label>
      <div class="bubbles-wrap">
        <button 
          *ngFor="let reason of reasons" 
          type="button" 
          class="reason-bubble" 
          [class.selected]="selectedReasonCategory === reason"
          (click)="selectReason(reason)">
          {{ reason }}
        </button>
      </div>

      <!-- Additional Details Text Area -->
      <div class="form-group description-box">
        <label for="reasonTextInput" class="field-label">
          Incident Details <span class="required-star">*</span>
        </label>
        <p class="field-guideline">
          Please describe what went wrong (e.g., no prints, wrong photos, booth froze). If you don't have the receipt QR, also include the store location, approximate time, and amount paid. If you paid by credit/debit card, please attach or reference your bank transaction record (statement line, transaction ID, or screenshot) so we can verify the charge.
        </p>
        <textarea 
          id="reasonTextInput" 
          class="form-textarea" 
          rows="4" 
          maxlength="200"
          placeholder="Please describe what happened in detail (required, max 200 characters)..." 
          [(ngModel)]="reasonText"
          required>
        </textarea>
        <div class="char-counter" [class.near-limit]="reasonText.length > 180">
          {{ reasonText.length || 0 }} / 200 characters
        </div>
      </div>

      <!-- Additional Files to Upload (KAN-24) -->
      <div class="evidence-attachment-group">
        <div class="evidence-header-row">
          <label class="field-label">
            Additional Files to Upload <span class="optional-tag">(Optional)</span>
          </label>
          <span class="evidence-count-badge">{{ selectedEvidenceFiles.length }}/{{ MAX_ATTACHMENTS }}</span>
        </div>
        <p class="field-guideline">
          Attach receipts, card transaction screenshots, or defective print photos (JPEG, PNG, or WebP &le; 5 MB each, up to 3 files).
        </p>

        <!-- Hidden Native File Input -->
        <input 
          #evidenceFileInput
          type="file" 
          class="file-input-hidden" 
          accept="image/jpeg,image/png,image/webp" 
          multiple
          (change)="onEvidenceFilesSelected($event)" />

        <!-- Upload Trigger Button (Hidden if max attachments reached) -->
        <div *ngIf="selectedEvidenceFiles.length < MAX_ATTACHMENTS" class="evidence-picker-box">
          <button 
            type="button" 
            class="attach-file-btn" 
            (click)="evidenceFileInput.click()">
            <svg class="attach-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
            <span>+ Upload Additional Files</span>
          </button>
        </div>

        <!-- Selected Files Previews Grid -->
        <div *ngIf="selectedEvidenceFiles.length > 0" class="evidence-previews-grid">
          <div *ngFor="let item of selectedEvidenceFiles; let i = index" class="evidence-preview-card">
            <div class="preview-img-wrap">
              <img [src]="item.previewUrl" [alt]="item.file.name" class="evidence-thumb-img" />
              <button 
                type="button" 
                class="remove-evidence-btn" 
                title="Remove file" 
                (click)="removeEvidenceFile(i)">
                &times;
              </button>
            </div>
            <div class="preview-meta">
              <span class="preview-filename" [title]="item.file.name">{{ item.file.name }}</span>
              <span class="preview-filesize">{{ item.sizeFormatted }}</span>
            </div>
          </div>
        </div>

        <!-- File Error Message -->
        <div *ngIf="evidenceError" class="evidence-error-banner">
          <svg class="error-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ evidenceError }}</span>
        </div>
      </div>


      <!-- Payout Preference / Coupon vs Cash Notice Box (Hidden for now) -->
      <div *ngIf="showCashRefundOption" class="payout-notice-card">
        <div class="notice-header">
          <span class="notice-icon">🎫</span>
          <h4 class="notice-title">Refund Method Notice</h4>
        </div>
        <p class="notice-body">
          We are going to issue a coupon for your refund. If you want a cash refund, it will take longer to review the approval.
        </p>
        <label class="cash-opt-in-label">
          <input 
            type="checkbox" 
            class="cash-checkbox" 
            [(ngModel)]="wantsCashRefund" />
          <span class="checkbox-text">I still want it in cash</span>
        </label>
      </div>
    </div>

    <!-- Error Banner -->
    <div *ngIf="submissionError" class="error-banner">
      <svg class="error-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ submissionError }}</span>
    </div>

    <!-- Submit Action -->
    <div class="form-actions">
      <button 
        type="button" 
        class="btn btn-primary submit-btn" 
        [disabled]="isSubmitting" 
        (click)="submitRefund()">
        <span *ngIf="!isSubmitting">Submit Request</span>
        <span *ngIf="isSubmitting">Submitting Request...</span>
      </button>
    </div>
  </div>

  <!-- STEP 4: SUBMISSION CONFIRMATION -->
  <div *ngIf="currentStep === 'confirmed'" class="card confirmation-card">
    
    <!-- State 1: APPROVED with Auto-Issued Coupon -->
    <ng-container *ngIf="liveRefundDoc?.status === 'approved' && liveRefundDoc?.couponCode">
      <div class="success-icon-wrap celebration-icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#16a34a" stroke-width="2.5">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      </div>
      <h2 class="confirmation-title approved-title">Replacement Voucher Approved!</h2>
      <p class="confirmation-desc">
        Your photo session was successfully verified and your single-use replacement voucher is ready to use immediately.
      </p>

      <!-- AI Findings Card -->
      <div *ngIf="liveRefundDoc?.aiSummary" class="ai-findings-card">
        <div class="ai-badge-header">
          <span class="ai-chip">🤖 AI Verification</span>
          <span class="verification-badge">Validation Passed</span>
        </div>
        <p class="ai-summary-text">{{ liveRefundDoc.aiSummary }}</p>
      </div>

      <!-- Voucher Display Card -->
      <div class="coupon-display-card">
        <span class="coupon-tag">
          {{ liveRefundDoc.eligibleCouponAmount >= 10 ? 'Free Photo Session' : ('$' + liveRefundDoc.eligibleCouponAmount.toFixed(2) + ' OFF') }}
        </span>
        <div class="coupon-code-wrap">
          <span class="coupon-code-text">{{ formatCouponCode(liveRefundDoc.couponCode) }}</span>
          <button type="button" class="copy-code-btn" [class.copied]="isCopiedCoupon" (click)="copyCouponCode(liveRefundDoc.couponCode)">
            <span *ngIf="!isCopiedCoupon">📋 Copy Code</span>
            <span *ngIf="isCopiedCoupon">✓ Copied!</span>
          </button>
        </div>
        <p class="coupon-meta-note">
          Valid for 1 year across all K-Photo LaVie booths. A copy of this voucher has also been sent to <strong>{{ userEmail }}</strong>.
        </p>
      </div>
    </ng-container>

    <!-- State 2: DENIED -->
    <ng-container *ngIf="liveRefundDoc?.status === 'denied'">
      <div class="denied-icon-wrap">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#dc2626" stroke-width="2.5">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>
      <h2 class="confirmation-title denied-title">Request Could Not Be Approved</h2>
      <p class="confirmation-desc">
        {{ liveRefundDoc?.decisionRationale || 'This request does not meet our automated refund criteria.' }}
      </p>
      <div *ngIf="liveRefundDoc?.aiSummary" class="ai-findings-card denied-note">
        <div class="ai-badge-header">
          <span class="ai-chip">🤖 Policy Verification</span>
        </div>
        <p class="ai-summary-text">{{ liveRefundDoc.aiSummary }}</p>
      </div>
      <p class="support-contact-note">
        If you believe this was an error, please contact customer support directly at <strong>support&#64;kphotolavie.com</strong>.
      </p>
    </ng-container>

    <!-- State 3: PENDING / NEEDS_REVIEW (Initial or after evaluation) -->
    <ng-container *ngIf="liveRefundDoc?.status !== 'approved' && liveRefundDoc?.status !== 'denied'">
      <div class="success-icon-wrap">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#2563eb" stroke-width="2.5">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      </div>
      <h2 class="confirmation-title">
        {{ liveRefundDoc?.processedAt ? 'Refund Request Under Review' : 'Submitting & Validating...' }}
      </h2>
      <p class="confirmation-desc">
        {{ liveRefundDoc?.processedAt ? 'Your request has been received and routed to our support team for review.' : 'Our AI verification engine is checking machine telemetry and session logs...' }}
      </p>

      <!-- AI Findings Card if processed into needs_review -->
      <div *ngIf="liveRefundDoc?.aiSummary" class="ai-findings-card review-note">
        <div class="ai-badge-header">
          <span class="ai-chip">🤖 AI Validation Findings</span>
          <span class="review-badge">Queued for Review</span>
        </div>
        <p class="ai-summary-text">{{ liveRefundDoc.aiSummary }}</p>
      </div>

      <div class="confirmation-meta-box">
        <div class="meta-row">
          <span class="meta-label">Request ID</span>
          <span class="meta-value">{{ generatedRequestId }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">Recipient Email</span>
          <span class="meta-value">{{ userEmail }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">Current Status</span>
          <span class="status status-sent">In Review</span>
        </div>
      </div>

      <p class="confirmation-note">
        Staff review typically takes 1 to 2 business days. Updates will be sent to <strong>{{ userEmail }}</strong>.
      </p>
    </ng-container>

    <div class="confirmation-actions">
      <a routerLink="/home/refund-status" class="btn btn-primary">Track Refund Status</a>
      <a routerLink="/home/gallery" class="btn btn-secondary">Return to My Gallery</a>
      <button type="button" class="btn btn-secondary" (click)="resetForm()">Submit Another Request</button>
    </div>
  </div>
</div>
